/**
 * Shared walk-in / calendar selection helpers.
 * Keep selectAllow and handleDateSelect in sync via these functions.
 */

/** Allowed Ad-Hoc durations (must match CreateWalkInRoomBookingFilter). */
export const WALK_IN_DURATIONS_MINUTES = [30, 45, 60, 75, 90, 105, 120];

/** One 15-minute slot in milliseconds. */
const QUARTER_HOUR_MS = 15 * 60 * 1000;

/** Longest allowed walk-in duration - a longer drag gets snapped down to it. */
export const WALK_IN_MAX_DURATION_MINUTES =
	WALK_IN_DURATIONS_MINUTES[WALK_IN_DURATIONS_MINUTES.length - 1];

/**
 * Floor a date to the start of its 15-minute slot.
 * @param {Date} date
 * @returns {Date}
 */
export function floorToQuarterHour(date) {
	const floored = new Date(date);
	floored.setSeconds(0, 0);
	floored.setMinutes(floored.getMinutes() - (floored.getMinutes() % 15));
	return floored;
}

/**
 * End time a running walk-in booking gets when "Beenden" is pressed at `now`: the end of the
 * 15-minute slot after the current one (must match RoomBookingWalkInApiController::endPutAction).
 * Ended at 11:29 it runs until 11:45, ended at 11:31 until 12:00.
 *
 * @param {object|null} booking
 * @param {Date} [now]
 * @returns {Date|null} null if the booking has no end time
 */
export function walkInEndAfterEarlyEnd(booking, now = new Date()) {
	if (!booking?.endTime) {
		return null;
	}

	const endOfNextSlot = new Date(
		floorToQuarterHour(now).getTime() + 2 * QUARTER_HOUR_MS,
	);
	const minimumEnd = new Date(
		new Date(booking.startTime).getTime() + QUARTER_HOUR_MS,
	);

	return endOfNextSlot > minimumEnd ? endOfNextSlot : minimumEnd;
}

/**
 * @param {number} minutes
 * @returns {number}
 */
export function snapToAllowedWalkInDuration(minutes) {
	return WALK_IN_DURATIONS_MINUTES.reduce((closest, allowed) =>
		Math.abs(allowed - minutes) < Math.abs(closest - minutes) ? allowed : closest
	);
}

/**
 * @param {object} options
 * @param {Date} options.selectStart
 * @param {Date} options.selectEnd
 * @param {string|null} options.userRole
 * @param {number} [options.earliestBookingSeconds=900]
 * @param {Date} [options.now]
 * @returns {{ allowed: boolean, isWalkIn: boolean, reason?: string }}
 */
export function evaluateCalendarSelection({
	selectStart,
	selectEnd,
	userRole,
	earliestBookingSeconds = 900,
	now = new Date(),
}) {
	const slotStart = floorToQuarterHour(now);
	const walkInWindowEnd = new Date(now.getTime() + earliestBookingSeconds * 1000);

	// Entirely in the past (ended at or before current slot start)
	if (selectEnd <= slotStart) {
		return { allowed: false, isWalkIn: false, reason: "past" };
	}

	// Drag started before the current 15-minute slot (e.g. 4 hours ago)
	if (selectStart < slotStart) {
		return { allowed: false, isWalkIn: false, reason: "past-start" };
	}

	const isWalkIn = userRole === "student" && selectStart < walkInWindowEnd;

	return { allowed: true, isWalkIn };
}

/**
 * Build end-time chip options from floored "now".
 * @param {Date} [now]
 * @returns {{ durationMinutes: number, endTime: Date, endLabel: string, durationLabel: string }[]}
 */
export function buildWalkInEndTimeOptions(now = new Date()) {
	const start = floorToQuarterHour(now);

	return WALK_IN_DURATIONS_MINUTES.map((durationMinutes) => {
		const endTime = new Date(start.getTime() + durationMinutes * 60000);
		const endLabel = endTime.toLocaleTimeString("de-DE", {
			hour: "2-digit",
			minute: "2-digit",
		});
		// Keep secondary line short so chips stay scannable
		const durationLabel = `${durationMinutes} Min`;

		return { durationMinutes, endTime, endLabel, durationLabel };
	});
}
